import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { TreeStructure, Plus, LinkSimple, LinkBreak, ArrowSquareOut, MagnifyingGlass, Globe, Receipt, FilePdf } from "@phosphor-icons/react";
import ConsolidatedInvoiceDialog from "./ConsolidatedInvoiceDialog";
import AutoConsolidatePanel from "./AutoConsolidatePanel";
import { SubClientCard, SubClientLinksProvider, adminLinks } from "./portal/SubClientCards";
import { fmtMoney } from "./portal/portalUtils";
import { toast } from "sonner";

/* Admin: Main client ↔ sub-client hierarchy panel on the client detail page.
   - Sub-client: shows its main client with Move / Detach.
   - Main or standalone client: lists sub-clients, "Add existing client", "New sub-client". */
export default function SubClientsPanel({ client, onChanged }) {
  const navigate = useNavigate();
  const [picker, setPicker] = useState(false);
  const [moveTo, setMoveTo] = useState(false);
  const [consolidated, setConsolidated] = useState(false);
  const [overview, setOverview] = useState(null);
  const subCount = (client.sub_clients || []).length;
  useEffect(() => {
    if (client.parent || !subCount) { setOverview(null); return; }
    API.get(`/clients/${client.id}/sub-clients/overview`).then(({ data }) => setOverview(data)).catch(() => setOverview({ sub_clients: [] }));
  }, [client.id, client.parent, subCount]);
  const openStatement = async () => {
    try {
      const month = window.prompt("Statement month (YYYY-MM)", new Date().toISOString().slice(0, 7));
      if (!month) return;
      const { data } = await API.get(`/clients/${client.id}/statement.pdf`, { params: { month }, responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't build the statement"); }
  };

  const detach = async () => {
    if (!window.confirm(`Detach ${client.company_name || client.first_name} from ${client.parent?.name}? It becomes a standalone client with its own branding again.`)) return;
    try {
      await API.put(`/clients/${client.id}/parent`, { parent_client_id: null });
      toast.success("Detached from main client");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not detach"); }
  };

  const detachSub = async (sub) => {
    if (!window.confirm(`Remove ${sub.name} from this main client? It becomes standalone again.`)) return;
    try {
      await API.put(`/clients/${sub.id}/parent`, { parent_client_id: null });
      toast.success(`${sub.name} detached`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not detach"); }
  };

  if (client.parent) {
    return (
      <div className="mb-6 border border-violet-300 dark:border-violet-900 rounded-lg overflow-hidden" data-testid="sub-client-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-violet-500/5">
          <div className="flex items-center gap-3 min-w-0">
            <TreeStructure size={20} className="text-violet-600 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold font-[Chivo]">Sub-client of <Link to={`/clients/${client.parent.id}`} className="text-primary hover:underline" data-testid="sub-client-parent-link">{client.parent.name}</Link></h2>
              <p className="text-[10px] text-muted-foreground mt-0.5">Uses {client.parent.name}'s branding everywhere · their portal users see this client's work · invoices are billed to {client.parent.name}.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMoveTo(true)} data-testid="sub-client-move-btn"><LinkSimple size={12} className="mr-1" /> Move to another main client</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={detach} data-testid="sub-client-detach-btn"><LinkBreak size={12} className="mr-1" /> Detach</Button>
          </div>
        </div>
        {moveTo && <ClientPickerDialog title={`Move ${client.company_name || client.first_name} under…`} single excludeIds={[client.id, client.parent.id]} onClose={() => setMoveTo(false)}
          onConfirm={async (ids) => { await API.put(`/clients/${client.id}/parent`, { parent_client_id: ids[0] }); toast.success("Moved"); setMoveTo(false); onChanged?.(); }} />}
      </div>
    );
  }

  const subs = client.sub_clients || [];
  return (
    <div className="mb-6 border border-border rounded-lg overflow-hidden" data-testid="sub-clients-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-muted/20">
        <div className="flex items-center gap-3 min-w-0">
          <TreeStructure size={20} className={subs.length ? "text-violet-600" : "text-primary"} />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold font-[Chivo]">{subs.length ? "Main client" : "Sub-clients"} {subs.length ? <span className="text-muted-foreground font-normal">· {subs.length} sub-client{subs.length === 1 ? "" : "s"}</span> : null}</h2>
            <p className="text-[10px] text-muted-foreground mt-0.5">White-label FM: add the clients you look after for this company. They'll see this client's branding, this client's portal sees all their work, and their invoices are billed here.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {subs.length > 0 && (
            <>
              <Button size="sm" variant="outline" className="h-7 text-xs border-violet-300 text-violet-700 dark:text-violet-300" onClick={() => setConsolidated(true)} data-testid="sub-clients-consolidated-btn"><Receipt size={12} className="mr-1" /> Consolidated invoice</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openStatement} data-testid="sub-clients-statement-btn"><FilePdf size={12} className="mr-1" /> Monthly statement</Button>
            </>
          )}
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPicker(true)} data-testid="sub-clients-add-existing-btn"><LinkSimple size={12} className="mr-1" /> Add existing client</Button>
          <Button size="sm" className="h-7 text-xs" onClick={() => navigate(`/clients?new=1&parent=${client.id}`)} data-testid="sub-clients-new-btn"><Plus size={12} className="mr-1" /> New sub-client</Button>
        </div>
      </div>
      {subs.length > 0 && (
        <div className="p-4" data-testid="sub-clients-list">
          {overview && overview.sub_clients.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="sub-clients-totals">
              {(() => { const t = overview.sub_clients.reduce((a, x) => ({ open: a.open + (x.counts?.open_requests || 0), jobs: a.jobs + (x.counts?.active_jobs || 0), emg: a.emg + (x.counts?.active_emergencies || 0), out: a.out + (x.counts?.outstanding_amount || 0) }), { open: 0, jobs: 0, emg: 0, out: 0 });
                return [["Sub-clients", subs.length], ["Open requests", t.open], ["Active jobs", t.jobs], ["Live emergencies", t.emg, t.emg > 0 ? "text-red-600" : ""], ["Outstanding", fmtMoney(t.out), t.out > 0 ? "text-amber-600" : ""]].map(([l, v, cls]) => (
                  <div key={l} className="border border-border rounded-md bg-card px-3 py-2"><p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{l}</p><p className={`text-lg font-bold font-[Chivo] ${cls || ""}`}>{v}</p></div>
                )); })()}
            </div>
          )}
          <SubClientLinksProvider links={adminLinks(client.id)}>
            <div className="grid md:grid-cols-2 gap-4">
              {subs.map((s) => {
                const rich = overview?.sub_clients.find((x) => x.id === s.id);
                const sub = rich || { id: s.id, display_name: s.name, contact_name: `${s.first_name || ""} ${s.last_name || ""}`.trim(), email: s.email, counts: null };
                return (
                  <SubClientCard key={s.id} sub={sub} footer={
                    <div className="flex flex-wrap items-center justify-between gap-2" data-testid={`sub-client-row-${s.id}`}>
                      <div className="flex items-center gap-1.5">
                        {s.portal_enabled && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 inline-flex items-center gap-0.5"><Globe size={10} /> Portal</span>}
                        {s.po_exempt && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700" title="Doesn't issue PO/WO numbers — exempt from the PO-required rule" data-testid={`sub-client-po-exempt-${s.id}`}>No PO/WOs</span>}
                        {!overview && <span className="text-[10px] text-muted-foreground">Loading activity…</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(`/clients/${s.id}`)} data-testid={`sub-client-link-${s.id}`}><ArrowSquareOut size={12} className="mr-1" /> Client record</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => detachSub(s)} data-testid={`sub-client-detach-${s.id}`}><LinkBreak size={12} className="mr-1" /> Detach</Button>
                      </div>
                    </div>
                  } />
                );
              })}
            </div>
          </SubClientLinksProvider>
        </div>
      )}
      {subs.length > 0 && <AutoConsolidatePanel client={client} />}
      {picker && <ClientPickerDialog title={`Add existing clients under ${client.company_name || client.first_name}`} excludeIds={[client.id]} onClose={() => setPicker(false)}
        onConfirm={async (ids) => { const { data } = await API.post(`/clients/${client.id}/sub-clients`, { client_ids: ids }); toast.success(`${data.moved.length} client${data.moved.length === 1 ? "" : "s"} added as sub-client${data.moved.length === 1 ? "" : "s"}`); setPicker(false); onChanged?.(); }} />}
      {consolidated && <ConsolidatedInvoiceDialog client={client} open={consolidated} onClose={() => setConsolidated(false)} />}
    </div>
  );
}

/* Pick standalone clients (never existing main clients or sub-clients) to move under a main client. */
function ClientPickerDialog({ title, excludeIds = [], single = false, onConfirm, onClose }) {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/clients?limit=1000").then(({ data }) => setAll((data.clients || []).filter((c) => !excludeIds.includes(c.id) && (single ? c.client_kind !== "sub" : c.client_kind === "standalone")))).catch(() => setAll([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = (all || []).filter((c) => !q || `${c.company_name} ${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked((p) => single ? [id] : (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const confirm = async () => {
    if (!picked.length) return;
    setSaving(true);
    try { await onConfirm(picked); } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="sub-client-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">{title}</DialogTitle>
          <DialogDescription>{single ? "Choose the main client to move this client under." : "Only standalone clients are listed — a client can belong to one main client, and a main client can't be nested."}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" className="pl-8 h-8 text-xs" data-testid="sub-client-picker-search" />
        </div>
        <div className="max-h-72 overflow-y-auto border border-border rounded-md divide-y divide-border">
          {all === null ? <p className="p-3 text-xs text-muted-foreground">Loading…</p> : rows.length === 0 ? <p className="p-3 text-xs text-muted-foreground">No matching clients.</p> : rows.map((c) => (
            <label key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/30" data-testid={`sub-client-pick-${c.id}`}>
              <input type={single ? "radio" : "checkbox"} checked={picked.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`sub-client-pick-input-${c.id}`} />
              <span className="min-w-0 flex-1">
                <span className="block font-medium truncate">{c.company_name || `${c.first_name} ${c.last_name}`}{c.client_kind === "main" ? <span className="ml-1.5 text-[9px] uppercase text-violet-700 bg-violet-500/10 px-1 rounded">main · {c.sub_client_count}</span> : null}</span>
                <span className="block text-[10px] text-muted-foreground truncate">{[`${c.first_name || ""} ${c.last_name || ""}`.trim(), c.email].filter(Boolean).join(" · ")}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={!picked.length || saving} onClick={confirm} data-testid="sub-client-picker-confirm-btn">{saving ? "Saving…" : single ? "Move here" : `Add ${picked.length || ""} as sub-client${picked.length === 1 ? "" : "s"}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
